import { useState, useEffect } from 'react';
import { api } from "@/lib/api";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { shiftApi } from "@/api/shift";
import {
    formatIstTimeHHmm,
    generateIstSlotTimes,
    istDayRangeIsoForApi,
    istWeekdayName,
    toIstDateInputValue,
} from "@/utils/dateUtils";

interface TimeSlot {
    time: string;
    status: 'AVAILABLE' | 'PARTIAL' | 'FULL';
    slotId?: string;
}

interface TempShift {
    id: string;
    startTime: string;
    endTime: string;
    staffId: string;
    hospitalId: string;
    status: string;
    createdAt: string;
    updatedAt: string;
}

interface Doctor {
    id: string;
    name: string;
    specialisation: string;
    shifts: Array<{
        id: string;
        shiftName: string;
        day: string;
        startTime: string;
        endTime: string;
    }>;
}

interface DoctorSlotsProps {
    doctorId: string;
    selectedDate: string;
    onSlotSelect: (time: string, slotId: string, isPartiallyBooked: boolean) => void;
    /** When editing, ignore this appointment when marking slots full/partial. */
    excludeAppointmentId?: string;
    /** Booking hospital (chain staff may book at a site other than their linked hospital). */
    hospitalId?: string;
}

export default function DoctorSlots({
    doctorId,
    selectedDate,
    onSlotSelect,
    excludeAppointmentId,
    hospitalId,
}: DoctorSlotsProps) {
    const [availableTimeSlots, setAvailableTimeSlots] = useState<TimeSlot[]>([]);
    const [noShiftFound, setNoShiftFound] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>("");

    const fetchDoctorSlots = async (doctorId: string, date: string, bookingHospitalId?: string) => {
        const { startDate, endDate } = istDayRangeIsoForApi(date);

        try {
            const response = await api.get(`/api/doctor/get-slots/${doctorId}`, {
                params: {
                    startDate,
                    endDate,
                    ...(bookingHospitalId ? { hospitalId: bookingHospitalId } : {}),
                }
            });
            return response.data?.data || [];
        } catch (error) {
            console.error('Error fetching doctor slots:', error);
            return [];
        }
    };

    const fetchDoctorDetails = async (doctorId: string) => {
        try {
            const response = await api.get(`/api/hospital-admin/staff/fetch/${doctorId}`);
            console.log('doctor details', response.data?.data);
            console.log('doctor details', response.data?.data.shifts);
            return response.data?.data;
        } catch (error) {
            console.error('Error fetching doctor details:', error);
            throw error;
        }
    };

    const fetchDoctorTempShifts = async (doctorId: string) => {
        try {
            const response = await shiftApi.getTempShiftByStaff(doctorId);

            if (response.data) {
                let tempShiftsData = [];

                if (response.data.data) {
                    tempShiftsData = response.data.data.tempShifts ||
                        response.data.data.shifts ||
                        response.data.data || [];
                } else if (Array.isArray(response.data)) {
                    tempShiftsData = response.data;
                } else if (response.data.tempShifts) {
                    tempShiftsData = response.data.tempShifts;
                } else if (response.data.shifts) {
                    tempShiftsData = response.data.shifts;
                }

                console.log('Doctor temp shifts data:', tempShiftsData);
                return Array.isArray(tempShiftsData) ? tempShiftsData : [];
            }

            return [];
        } catch (error) {
            console.error('Error fetching doctor temp shifts:', error);
            return [];
        }
    };

    const generateTimeSlots = async (doctor: Doctor, date: string, bookingHospitalId?: string) => {
        // First check for temporary shifts for the selected date
        const tempShifts = await fetchDoctorTempShifts(doctor.id);
        console.log('Temp shifts for doctor:', tempShifts);

        const tempShiftForDate = tempShifts.find(
            (tempShift: TempShift) => toIstDateInputValue(tempShift.startTime) === date
        );

        let shiftStartTime: string = '';
        let shiftEndTime: string = '';
        let shiftFound = false;

        if (tempShiftForDate) {
            // Use temporary shift timing
            console.log('Using temp shift for date:', tempShiftForDate);
            shiftStartTime = formatIstTimeHHmm(tempShiftForDate.startTime);
            shiftEndTime = formatIstTimeHHmm(tempShiftForDate.endTime);
            shiftFound = true;
        } else {
            const selectedDay = istWeekdayName(date);
            const doctorShift = doctor.shifts.find(shift => shift.day === selectedDay);
            console.log('doctorShift', doctorShift);

            if (doctorShift) {
                shiftStartTime = doctorShift.startTime;
                shiftEndTime = doctorShift.endTime;
                shiftFound = true;
            }
        }

        if (!shiftFound) {
            setNoShiftFound(true);
            setAvailableTimeSlots([]);
            return;
        }

        try {
            const slotTimes = generateIstSlotTimes(shiftStartTime, shiftEndTime);
            const slots: TimeSlot[] = slotTimes.map((time) => ({
                time,
                status: 'AVAILABLE' as const,
            }));

            const bookedSlots = await fetchDoctorSlots(doctor.id, date, bookingHospitalId);

            const availableTimeSlots = slots.map(slot => {
                const bookedSlot = bookedSlots.find((bs: { timeSlot: string }) => {
                    return slot.time === formatIstTimeHHmm(bs.timeSlot);
                });

                if (bookedSlot) {
                    const bookedIds = [
                        bookedSlot.appointment1?.id ?? bookedSlot.appointment1Id,
                        bookedSlot.appointment2?.id ?? bookedSlot.appointment2Id,
                    ].filter(Boolean) as string[];
                    const otherBooked = excludeAppointmentId
                        ? bookedIds.filter((id) => id !== excludeAppointmentId)
                        : bookedIds;

                    if (otherBooked.length >= 2) {
                        return { ...slot, status: 'FULL' as const, slotId: bookedSlot.id };
                    }
                    if (otherBooked.length === 1) {
                        return { ...slot, status: 'PARTIAL' as const, slotId: bookedSlot.id };
                    }
                }
                return { ...slot, status: 'AVAILABLE' as const };
            });

            setAvailableTimeSlots(availableTimeSlots);
            setNoShiftFound(false);
        } catch (error) {
            setError('Failed to generate time slots');
            setNoShiftFound(true);
            setAvailableTimeSlots([]);
        }
    };

    const handleTimeSlotSelect = (time: string) => {
        setSelectedTimeSlot(time);
        const selectedSlot = availableTimeSlots.find(slot => slot.time === time);
        if (selectedSlot) {
            onSlotSelect(
                time,
                selectedSlot.slotId || '',
                selectedSlot.status === 'PARTIAL'
            );
        }
    };

    useEffect(() => {
        const loadSlots = async () => {
            if (!doctorId || !selectedDate) {
                setAvailableTimeSlots([]);
                return;
            }

            setLoading(true);
            setError(null);
            try {
                const doctorDetails = await fetchDoctorDetails(doctorId);
                await generateTimeSlots(doctorDetails, selectedDate, hospitalId);
            } catch (err) {
                setError('Failed to load slots');
            } finally {
                setLoading(false);
            }
        };

        loadSlots();
    }, [doctorId, selectedDate, hospitalId, excludeAppointmentId]);

    if (loading) {
        return <div className="text-center py-4">Loading slots...</div>;
    }

    if (error) {
        return <div className="text-red-500 py-4">{error}</div>;
    }

    if (noShiftFound) {
        return <div className="text-yellow-600 py-4">No shifts available for this date</div>;
    }

    return (
        <div className="space-y-2">
            <Label>Available Time Slots</Label>
            <div className="">
                <RadioGroup
                    value={selectedTimeSlot}
                    onValueChange={handleTimeSlotSelect}
                    className="flex flex-wrap gap-2"
                >
                    {availableTimeSlots.map((slot) => (
                        <div key={slot.time} className="flex-none">
                            <RadioGroupItem
                                value={slot.time}
                                id={slot.time}
                                disabled={slot.status === 'FULL'}
                                className="peer sr-only"
                            />
                            <Label
                                htmlFor={slot.time}
                                className={`flex h-10 w-20 cursor-pointer items-center justify-center rounded-md border-2 ${slot.status === 'AVAILABLE'
                                    ? 'border-green-500 bg-green-50 hover:bg-green-100'
                                    : slot.status === 'PARTIAL'
                                        ? 'border-orange-500 bg-orange-50 hover:bg-orange-100'
                                        : 'border-red-500 bg-red-50 opacity-50 cursor-not-allowed'
                                    } p-2 text-sm peer-data-[state=checked]:border-primary peer-data-[state=checked]:text-primary`}
                            >
                                {slot.time}
                            </Label>
                        </div>
                    ))}
                </RadioGroup>
            </div>
        </div>
    );
} 